import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {useDispatch, useSelector} from '@modifi/redux'
import {FormActionsContext, FormDataContext} from '@modifi/plugin-forms'

import {selectCompanyIsLocked} from '../../../../data/company'
import {FormDataChangedBanner} from '../form-data-changed-banner'
import {hasConflictingChanges} from './utils'

type PendingCheck = {
  baseValuesWhenLocked: Record<string, any>
  changedInputs: string[]
  refetchSettled: boolean
}

type CompanyUnlockRefresherProps = {
  /** returns the actions which (force) fetch the data this form is based on */
  refetch: () => any[]
  initialValues?: Record<string, any>
  defaultValues?: Record<string, any>
}

/**
 * While the company is locked the admin team can change the data the user is editing. Once the
 * company gets unlocked the form data is refetched, the unsaved changes of the user are kept and
 * a banner lets the user decide about them when the server values of the inputs they changed are
 * different now.
 * Must be rendered inside a <Form>.
 */
const CompanyUnlockRefresher: React.FC<CompanyUnlockRefresherProps> = ({
  refetch,
  initialValues,
  defaultValues,
}) => {
  const dispatch = useDispatch()
  const isCompanyLocked = useSelector(selectCompanyIsLocked)
  const {changedInputs, hasChanges} = useContext(FormDataContext)
  const {discard} = useContext(FormActionsContext)

  const baseValues = useMemo(
    () => ({...defaultValues, ...initialValues}),
    [defaultValues, initialValues]
  )

  /* the lock transition effect below must only run when the lock status changes, so it reads the
  other values from here instead of depending on them */
  const latest = useRef({baseValues, hasChanges, changedInputs, refetch})
  latest.current = {baseValues, hasChanges, changedInputs, refetch}

  const wasCompanyLocked = useRef(isCompanyLocked)
  /* taken once when the company gets locked: the company itself is polled, so for forms based on it
  the changes of the admin team already arrive while it is still locked */
  const baseValuesWhenLocked = useRef<Record<string, any> | null>(
    isCompanyLocked ? baseValues : null
  )
  const [pendingCheck, setPendingCheck] = useState<PendingCheck | null>(null)
  const [isBannerVisible, setIsBannerVisible] = useState(false)

  useEffect(() => {
    const wasLocked = wasCompanyLocked.current
    wasCompanyLocked.current = isCompanyLocked

    if (!wasLocked && isCompanyLocked) {
      baseValuesWhenLocked.current = latest.current.baseValues
      return
    }
    if (!wasLocked || isCompanyLocked) {
      return
    }

    const {hasChanges: hadChanges, changedInputs: changedInputsOnUnlock} = latest.current
    const check: PendingCheck | null =
      hadChanges && baseValuesWhenLocked.current
        ? {
            baseValuesWhenLocked: baseValuesWhenLocked.current,
            changedInputs: changedInputsOnUnlock as string[],
            refetchSettled: false,
          }
        : null
    baseValuesWhenLocked.current = null
    setPendingCheck(check)

    Promise.allSettled(latest.current.refetch().map(action => dispatch(action))).then(() => {
      // a newer check (or none) might have replaced this one in the meantime
      setPendingCheck(current =>
        current === check && check ? {...check, refetchSettled: true} : current
      )
    })
  }, [isCompanyLocked, dispatch])

  useEffect(() => {
    if (!pendingCheck) {
      return
    }
    if (!hasChanges) {
      setPendingCheck(null)
    } else if (
      hasConflictingChanges(
        pendingCheck.baseValuesWhenLocked,
        baseValues,
        pendingCheck.changedInputs
      )
    ) {
      setPendingCheck(null)
      setIsBannerVisible(true)
    } else if (pendingCheck.refetchSettled) {
      // the refetched data did not touch the changes of the user
      setPendingCheck(null)
    }
  }, [pendingCheck, baseValues, hasChanges])

  // the decision is not needed anymore once the changes are gone (saved or discarded) or locked again
  useEffect(() => {
    if (!hasChanges || isCompanyLocked) {
      setIsBannerVisible(false)
    }
  }, [hasChanges, isCompanyLocked])

  const handleDiscard = useCallback(() => {
    discard?.()
    setIsBannerVisible(false)
  }, [discard])

  const handleKeep = useCallback(() => {
    setIsBannerVisible(false)
  }, [])

  return (
    <FormDataChangedBanner
      isVisible={isBannerVisible}
      onDiscard={handleDiscard}
      onKeep={handleKeep}
    />
  )
}

export default CompanyUnlockRefresher
